import L from 'leaflet';
import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import {
  Box,
  Checkbox,
  Chip,
  Drawer,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';

import axios from 'axios';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import PlaceDetail from './components/PlaceDetail';
import config from './config';
import { Place, PlaceType } from './Place';
import { useTheme, useMediaQuery } from '@mui/material';
import PlaceList from './components/PlaceList';
import { useUserLocation } from './components/useUserLocation';
import BottomModal from './components/BottomModal';
import PlaceListMobile from './components/PlaceListMobile';
import { LocateControl } from './components/LocateControl';
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}


const MapEvents = ({ setMap }: { setMap: (map: L.Map) => void }) => {
  const map = useMapEvents({});
  
  useEffect(() => {
    setMap(map);
  }, [map, setMap]);

  return null;
};
const Map: React.FC = () => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [filteredPlaces, setFilteredPlaces] = useState<Place[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [startIndex, setStartIndex] = useState(0);
  const [showFavorites, setShowFavorites] = useState(false); // Nuevo estado para favoritos
  const debouncedSearchText = useDebounce(searchText, 500); // 500ms de retraso
  const [map, setMap] = useState<L.Map | null>(null);

  // Obtener información del usuario del localStorage
  const userInfo: any = JSON.parse(localStorage.getItem("usuario") || "{}");
  const userGoogleId = userInfo?.id;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Categorías disponibles
  const categoryMapping = {
    Plaza: 'Plaza',
    ParqueTematico: 'Parque Temático',
    Campo: 'Campo',
    JuegosNiños: 'Juegos para Niños',
    CamasElasticas: 'Camas Elásticas',
    ParqueAcuatico: 'Parque Acuático',
    Museo: 'Museo',
    Granja: 'Granja',
    ParqueNatural: 'Parque Natural',
    EscapeRooms: 'Escape Rooms',
    Restaurante: 'Restaurante',
    Zoologicos: 'Zoológicos',
    Cine: 'Cine',
    Arcade: 'Arcade',
    Shopping: 'Shopping',
  };
  const categories = Object.keys(categoryMapping);

  useEffect(() => {
    fetchPlaces();
  }, [debouncedSearchText, selectedCategories, showFavorites]); // Agregamos showFavorites a las dependencias

  const fetchPlaces = async () => {
    try {
      let url = `${config.backendUrl}/api/Places`;
      const cacheKey = `placesCache_${userGoogleId || 'guest'}`;
      const params = new URLSearchParams();
      if (searchText) params.append("texto", searchText);
        selectedCategories.forEach((type) => params.append("placeTypes", type.toString()));
      
        
      // Si está activado el filtro de favoritos y hay usuario logueado
      if (showFavorites && userGoogleId) {
        url = `${config.backendUrl}/api/Places/favoritos/${userGoogleId}?${params.toString()}`;
      }
  
      // Construcción de la URL con parámetros

      const fullUrl = `${url}?${params.toString()}`;
      
      // Solo usamos caché cuando no hay filtros activos
      const shouldUseCache = !searchText && selectedCategories.length === 0 && !showFavorites;
  
      try {
        // Intentamos primero obtener datos del backend
        const response = await axios.get<Place[]>(fullUrl);
        const data = response.data;
        
        // Actualizamos el estado y la caché
        setPlaces(data);
        setFilteredPlaces(data);
        
        // Guardamos en caché solo si no hay filtros
        if (shouldUseCache) {
          localStorage.setItem(cacheKey, JSON.stringify({
            data,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        console.error('Error fetching places from backend:', error);
        
        // Solo intentamos usar caché si no hay filtros
        if (shouldUseCache) {
          const cachedData = localStorage.getItem(cacheKey);
          
          if (cachedData) {
            const { data, timestamp } = JSON.parse(cachedData);
            const cacheAge = Date.now() - timestamp;
            const maxCacheAge = 24 * 60 * 60 * 1000; // 24 horas
            
            if (cacheAge < maxCacheAge) {
              console.log('Using cached places data');
              setPlaces(data);
              setFilteredPlaces(data);
              return;
            } else {
              console.log('Cache expired, removing old data');
              localStorage.removeItem(cacheKey);
            }
          }
        }
        
        // Si no hay caché disponible o está expirado, mantener estado actual
        console.log('No cached data available or filters active');
      }
    } catch (error) {
      console.error('Unexpected error in fetchPlaces:', error);
    }
  };

  const handlePlaceClick = (place: Place) => {
    
    // Centrar el mapa si está disponible
    if (map) {
      map.flyTo([place.lat, place.longi], 17, {
        duration: 1,
        easeLinearity: 0.25
      });
    }
  };
  // Definir íconos personalizados
  const plazaIcon = new L.Icon({
    iconUrl: "/icons/plaza.png",
    iconSize: [20, 32],
    iconAnchor: [16, 32],
  });

  const museoIcon = new L.Icon({
    iconUrl: "/icons/museo.png",
    iconSize: [20, 32],
    iconAnchor: [16, 32],
  });

  const restauranteIcon = new L.Icon({
    iconUrl: "/icons/restaurante.png",
    iconSize: [20, 32],
    iconAnchor: [16, 32],
  });

  const ninosIcon = new L.Icon({
    iconUrl: "/icons/ninos.png",
    iconSize: [20, 32],
    iconAnchor: [16, 32],
  });

  const userIcon = new L.Icon({
    iconUrl: '/icons/user.png',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });

  const getIconByPlaceType = (placeType: PlaceType) => {
    console.log( placeType)
    console.log( PlaceType.Museo)
    const type = typeof placeType === 'string' 
    ? PlaceType[placeType as keyof typeof PlaceType]
    : placeType;

    switch (type) {
      case PlaceType.Plaza:
      case PlaceType.Zoologicos:
      case PlaceType.ParqueNatural:
      case PlaceType.Campo:
        return plazaIcon;
      case PlaceType.Museo:
        console.log("MUSEEOO")
        return museoIcon;
      case PlaceType.Restaurante:
        return restauranteIcon;
      default:
        console.log("DEFAULT")

        return ninosIcon;
    }
  };
  const createCustomIcon = (place: Place) => {
    console.log("(place.placeType", place.placeType )
    const iconUrl = getIconByPlaceType(place.placeType || PlaceType.JuegosNiños).options.iconUrl;
    
    return L.divIcon({
      html: `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        ">
          <img src="${iconUrl}" style="width: 20px; height: 28px;"/>
          <div style="
            background: white;
            border-radius: 4px;
            padding: 2px 4px;
            margin-top: 4px;
            font-size: 10px;
            font-weight: bold;
            white-space: nowrap;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          ">${place.name.length > 13 ? place.name.substring(0, 20) + '...' : place.name}</div>
        </div>
      `,
      className: '', // Elimina la clase por defecto para quitar estilos no deseados
      iconSize: [25, 32], // Tamaño del área del icono
      iconAnchor: [16, 32], // Punto de anclaje (centro abajo)
    });
  };
  
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);

  const handleMarkerClick = (placeId: string) => {
    setSelectedPlaceId(placeId);
    setDrawerOpen(true);
  };
  const hasFilters = searchText !== '' || selectedCategories.length > 0 || showFavorites;

  const userLocation = useUserLocation();


  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedPlace(null);
  };

  const toggleFavorites = () => {
    setShowFavorites(!showFavorites);
  };

  return (
    <Box sx={{ height: 'calc(100% - 9vh)', position: 'relative' }}>
      {/* Mapa */}
      <MapContainer
        center={[-34.6037, -58.3816]}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        ref={(map) => {
          if (map) {
            setMap(map);
          }
        }}
      >
          <MapEvents setMap={setMap} />

           <TileLayer
        url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
      />

      {/* Capa de etiquetas (nombres de calles) */}
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
      />
        {/* Grupo de marcadores con clustering */}
        <MarkerClusterGroup key={filteredPlaces.length}>
  {filteredPlaces.map((place) => (
    <Marker
      key={place.id}
      position={[place.lat, place.longi]}
      icon={createCustomIcon(place)}
      eventHandlers={{
        click: () => handleMarkerClick(place.id),
      }}
    >
      <Popup>
        <b>{place.name}</b>
        <br />
        {place.description}
      </Popup>
    </Marker>
  ))}
  {userLocation && (
    <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
      <Popup>Vos</Popup>
    </Marker>
  )}
</MarkerClusterGroup>     
<LocateControl userLocation={userLocation} />

 </MapContainer>

      {/* Contenedor para buscador y chip de favoritos */}
      <Box
         sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          zIndex: 1000,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row', // Columna en mobile, fila en desktop
          alignItems: 'flex-start',
          gap: 1
        }}
      >
        {/* Caja del buscador y filtros */}
        <Box
    sx={{
      backgroundColor: '#FFF',
      padding: '10px',
      borderRadius: '4px',
      boxShadow: 3,
      width: isMobile ? 'calc(100vw - 40px)' : 300, // Ancho completo en mobile
    }}
        >
          <TextField
            fullWidth
            label="Buscar lugar"
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            sx={{ marginBottom: 1 }}
          />

          {/* Filtro de categorías */}
          <Select
            id="demo-multiple-checkbox"
            multiple
            sx={{ padding: "1px",
              '& .MuiSelect-select': {
                padding: '6px'
              }
            }}
            value={selectedCategories}
            onChange={(e) => setSelectedCategories(e.target.value as string[])}
            input={<OutlinedInput sx={{ padding: '8px' }}/>}
            renderValue={(selected) =>
              selected.length === 0 ? (
                "Categorías"
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip
                      key={value}
                      label={categoryMapping[value as keyof typeof categoryMapping]}
                    />
                  ))}
                </Box>
              )
            }
            displayEmpty
          >
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                <Checkbox checked={selectedCategories.includes(category)} />
                <ListItemText primary={categoryMapping[category as keyof typeof categoryMapping]} />
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Chip de favoritos - ahora está fuera del contenedor del buscador */}
        {userGoogleId && (
    <Chip
      icon={<StarIcon />}
      label="Mis Favoritos"
      color={showFavorites ? "primary" : "default"}
      onClick={toggleFavorites}
      clickable
      
      sx={{
         backgroundColor: showFavorites ?'blue':'#FFF',
        boxShadow: 3,
        width: isMobile ? 'calc(100vw - 40px)' : 'auto', // Ancho completo en mobile
        '& .MuiChip-icon': {
          color: showFavorites ? '#fff' : 'inherit'
        }
      }}
    />
  )}

</Box>
      {/* Drawer para detalles (se mantiene igual) */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '80%' },
            boxSizing: 'border-box',
            top: '10vh !important',
            height: 'calc(90vh)',
            overflowY: 'auto',
          },
        }}
      >
        {selectedPlaceId && (
          <PlaceDetail id={selectedPlaceId} onClose={handleDrawerClose} />
        )}
      </Drawer>


{isMobile ? (
  <BottomModal open={hasFilters} onClose={() => {}}>
    <PlaceListMobile 
      places={filteredPlaces} 
      hasFilters={hasFilters}
      onPlaceClick={handlePlaceClick}
    />
  </BottomModal>
) : (
  <PlaceList 
    places={filteredPlaces} 
    hasFilters={hasFilters}
    isMobile={isMobile}
    onPlaceClick={handlePlaceClick}
  />
)}
    </Box>
  );
};


export default Map;